import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  MessageCircle, RefreshCw, Copy, Check, Trash2, PackagePlus, Search, Users, ClipboardList,
} from "lucide-react";

const WA_BASE = "https://wa.me";

export default function AvisosStock() {
  const [avisos, setAvisos] = useState([]);
  const [estado, setEstado] = useState("pendiente");
  const [dias, setDias] = useState(15);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/avisos-stock?estado=${estado}&dias=${dias}`);
      setAvisos(data.avisos || []);
      if (data.nuevos_generados > 0) {
        toast.success(`${data.nuevos_generados} ingreso(s) nuevo(s) detectado(s)`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [estado, dias]);
  useEffect(() => { load(); }, [load]);

  const visibles = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return avisos;
    return avisos.filter(a => [a.producto_nombre, a.laboratorio, a.numero_lote, a.documento_ref]
      .some(v => (v || "").toString().toLowerCase().includes(s)));
  }, [avisos, q]);

  const enviarWhatsapp = async (aviso, dest) => {
    if (!dest.telefono_wa) {
      toast.error(`${dest.vendedor_nombre} no tiene un celular válido registrado en Usuarios`);
      return;
    }
    window.open(`${WA_BASE}/${dest.telefono_wa}?text=${encodeURIComponent(dest.mensaje)}`, "_blank");
    try {
      await api.post(`/avisos-stock/${aviso.id}/enviado`, { vendedor_id: dest.vendedor_id });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copiar = async (dest) => {
    try {
      await navigator.clipboard.writeText(dest.mensaje);
      toast.success("Mensaje copiado");
    } catch { toast.error("No se pudo copiar el mensaje"); }
  };

  const descartar = async (aviso) => {
    if (!window.confirm(`¿Descartar el aviso de ${aviso.producto_nombre}?`)) return;
    try {
      await api.delete(`/avisos-stock/${aviso.id}`);
      toast.success("Aviso descartado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="avisos-stock-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Ventas · Bodega</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Avisos de stock por WhatsApp
          </h1>
          <p className="text-sm text-[#4a5c53] mt-1">
            Cada ingreso a bodega genera un aviso con el mensaje listo. Si el producto está pendiente en una
            cotización o nota de pedido, se avisa a ese vendedor; si no es de nadie, a todos los vendedores.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#5c7368] mb-1">Últimos días</div>
            <Input type="number" min="1" value={dias} className="w-24"
              onChange={(e) => setDias(Math.max(1, parseInt(e.target.value, 10) || 1))}
              data-testid="avisos-dias" />
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="avisos-refresh">
            <RefreshCw size={15} className={`mr-2 ${loading ? "animate-spin" : ""}`} />Actualizar
          </Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <Tabs value={estado} onValueChange={setEstado}>
          <TabsList className="bg-white border border-[#d1dbd6]">
            <TabsTrigger value="pendiente" data-testid="avisos-tab-pendiente">Por avisar</TabsTrigger>
            <TabsTrigger value="enviado" data-testid="avisos-tab-enviado">Avisados</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-full md:w-96">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#8ba397]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-8"
            placeholder="Buscar producto, laboratorio, lote o documento…" data-testid="avisos-search" />
        </div>
      </div>

      {visibles.length === 0 && !loading && (
        <div className="rounded-lg border-2 border-dashed border-[#d1dbd6] py-12 text-center text-sm text-[#8ba397]"
          data-testid="avisos-vacio">
          {estado === "pendiente"
            ? "No hay ingresos recientes por avisar. Amplíe el rango de días o registre una recepción."
            : "Todavía no ha avisado ningún ingreso."}
        </div>
      )}

      <div className="space-y-4">
        {visibles.map((a) => (
          <div key={a.id} className="rounded-lg border border-[#d1dbd6] bg-white overflow-hidden"
            data-testid={`aviso-${a.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 p-3 bg-[#f7f9f8] border-b border-[#d1dbd6]">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <PackagePlus size={16} className="text-[#205c45]" />
                  <span className="font-semibold text-[#15241d]">{a.producto_nombre || "—"}</span>
                  {a.destinatarios?.[0]?.motivo === "pendiente" ? (
                    <Badge className="bg-[#fef7ec] text-[#8a5500] border border-[#f5c477] text-[10px]">
                      <ClipboardList size={11} className="mr-1" />Pendiente de vendedor
                    </Badge>
                  ) : (
                    <Badge className="bg-[#eef1f7] text-[#2b3f63] border border-[#b9c4dc] text-[10px]">
                      <Users size={11} className="mr-1" />Aviso general
                    </Badge>
                  )}
                </div>
                <div className="text-[11px] text-[#5c7368] mt-0.5">
                  {a.laboratorio || "Sin laboratorio"}{a.presentacion ? ` · ${a.presentacion}` : ""}
                  {a.numero_lote ? ` · Lote ${a.numero_lote}` : ""}
                  {a.fecha_vencimiento ? ` · Vence ${a.fecha_vencimiento}` : ""}
                </div>
                <div className="text-[11px] text-[#5c7368]">
                  Ingresaron <strong className="text-[#205c45]">{a.cantidad} u.</strong>
                  {a.documento_ref ? ` · ${a.documento_ref}` : ""}{a.fecha_ingreso ? ` · ${a.fecha_ingreso}` : ""}
                </div>
              </div>
              <Button size="sm" variant="ghost" className="text-[#7a2514]" onClick={() => descartar(a)}
                data-testid={`descartar-aviso-${a.id}`}>
                <Trash2 size={14} className="mr-1" />Descartar
              </Button>
            </div>

            <div className="divide-y divide-[#eef2f0]">
              {(a.destinatarios || []).map((d) => (
                <div key={d.vendedor_id} className="p-3 space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-[#15241d]">{d.vendedor_nombre}</div>
                      <div className="text-[11px] text-[#5c7368]">
                        {d.telefono_wa ? `+${d.telefono_wa}` : "Sin celular registrado"}
                        {(d.pendientes || []).length > 0 && ` · ${d.pendientes.length} documento(s) pendiente(s)`}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {d.enviado && (
                        <Badge className="bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2] text-[10px]"
                          data-testid={`enviado-${a.id}-${d.vendedor_id}`}>
                          <Check size={11} className="mr-1" />Avisado
                        </Badge>
                      )}
                      <Button size="sm" variant="outline" onClick={() => copiar(d)}
                        data-testid={`copiar-${a.id}-${d.vendedor_id}`}>
                        <Copy size={13} className="mr-1" />Copiar
                      </Button>
                      <Button size="sm" className="bg-[#25D366] hover:bg-[#1ea952] text-white disabled:opacity-50"
                        disabled={!d.telefono_wa}
                        title={d.telefono_wa ? "Abrir WhatsApp con el mensaje" : "Registre el celular del vendedor en Usuarios"}
                        onClick={() => enviarWhatsapp(a, d)}
                        data-testid={`whatsapp-${a.id}-${d.vendedor_id}`}>
                        <MessageCircle size={14} className="mr-1" />WhatsApp
                      </Button>
                    </div>
                  </div>
                  {(d.pendientes || []).length > 0 && (
                    <div className="text-[11px] text-[#4a5c53] space-y-0.5">
                      {d.pendientes.map((p, i) => (
                        <div key={i}>
                          • {p.documento}{p.cliente_nombre ? ` · ${p.cliente_nombre}` : ""}
                          {p.cantidad ? ` · pendiente ${p.cantidad}` : ""}
                        </div>
                      ))}
                    </div>
                  )}
                  <pre className="text-[11px] whitespace-pre-wrap bg-[#f7f9f8] border border-[#d1dbd6] rounded p-2 text-[#15241d]"
                    data-testid={`mensaje-${a.id}-${d.vendedor_id}`}>{d.mensaje}</pre>
                </div>
              ))}
              {(a.destinatarios || []).length === 0 && (
                <div className="p-4 text-xs text-[#8ba397]">No hay vendedores activos para avisar.</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
